// Ongoing Order Screen — rendered for orders whose status has advanced past In Quotation
// (i.e. one of ORDER_STEPS). Layout mirrors the spec from the 2026-04 prompt:
//   1. OrderStatusBar (8 nodes incl. Order Started).
//   2. Read-only Order Basics header (same visual language as InQuotationPage).
//   3. Gantt (default-open) with the current step highlighted, a Today line,
//      a red overlay when state=TECHNICAL_DIFFICULTY, and a trailing Delivered milestone.
//   4. Order Steps table (six rows — one per ORDER_STEPS entry) with:
//        • Active radio (confirm → PUT /active-step)
//        • Step label + TD badge when applicable
//        • Quoted Start / Days / End (derived from the accepted quote's schedule)
//        • Actual Start / End (DateInput, persists to PUT /step-actuals/:step)
//        • Actual Duration (countWorkdays clamped at 0)
//        • Delta (Actual − Quoted) with green/yellow/red colour coding
//        • TD toggle button (active row only)
//   5. Cancel Order + Order Delivered (Finalize) buttons.
//
// All network writes return the fresh order payload so the UI stays in lock-step with the
// server — no optimistic merging needed.

import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Paper, Group, Text, Button, Table, Badge, Radio, Loader, Center, Alert, Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { api, imageUrl } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import OrderStatusBar, { type OrderStatus } from './OrderStatusBar';
import OrderGanttSection from './OrderGanttSection';
import type { QuotationSchedule } from './NewQuotationModal';
import type { OrderPart } from './OrderPartModal';
import {
  IconCustomer, IconTimer, IconSupply, IconExecute, IconPackage, IconTruck, IconDelivered,
} from '../../components/icons/CncIcons';
import { addWorkdays, countWorkdays, fromISODate, toISODate } from './workdays';
import StepNoteModal from './StepNoteModal';
import StepReportModal from './StepReportModal';

// The six ongoing-order steps. Must exactly match backend ORDER_STEPS and the CHECK constraint
// on order_step_actuals.step — drift here means rows silently fail to persist.
const ORDER_STEPS = [
  'Order Started', 'Supplying Consumables', 'In Execution',
  'Preparing to Ship', 'Shipped', 'Delivered',
] as const;
type OrderStep = typeof ORDER_STEPS[number];

interface StepActual {
  step: OrderStep;
  actualStartDate: string | null;
  actualEndDate: string | null;
  stepNote: string | null;
  reportNote: string | null;
  reportPaths: string[];
}

interface FullOrder {
  id: number;
  orderUid: string;
  customerId: number | null;
  customerName: string | null;
  customerLogo: string | null;
  contactName: string | null;
  technicianName: string | null;
  name: string;
  notes: string | null;
  imagePath: string | null;
  firstPartImagePath: string | null;
  orderDate: string;
  state: 'FINALIZED' | 'TECHNICAL_DIFFICULTY' | 'ONGOING' | 'CANCELLED';
  status: OrderStatus;
  technicalDifficultyStep: string | null;
  shipment: { useDefault: boolean; country: string | null; county: string | null; street: string | null; zip: string | null };
  parts: OrderPart[];
  stepActuals: StepActual[];
}

interface QuotationSummary {
  id: number;
  quotationUid: string;
  status: 'DRAFT' | 'SENT' | 'REFUSED' | 'ACCEPTED' | 'EXPORTED';
  totalPrice: number | null;
  totalCost: number | null;
}

interface QuotationDetail {
  id: number;
  quotationUid: string;
  schedule: QuotationSchedule;
}

const STEP_ICONS: Record<OrderStep, React.ComponentType<{ size?: number }>> = {
  'Order Started': IconTimer,
  'Supplying Consumables': IconSupply,
  'In Execution': IconExecute,
  'Preparing to Ship': IconPackage,
  'Shipped': IconTruck,
  'Delivered': IconDelivered,
};

function fmtDate(d: Date | null): string {
  if (!d) return '—';
  return dayjs(d).format('YYYY-MM-DD');
}

// Delta colour scale mirrors the spec: green when on/ahead-of-schedule, yellow for
// 1–3 slipped workdays, red for 4+. Null → neutral gray.
function deltaColor(delta: number | null): string {
  if (delta == null) return 'gray';
  if (delta <= 0) return 'green';
  if (delta <= 3) return 'yellow';
  return 'red';
}

export default function OngoingOrderPage() {
  const { id } = useParams();
  const orderId = Number(id);
  const navigate = useNavigate();
  const { me } = useAuth();
  const writable = canWrite(me?.role);

  const [order, setOrder] = useState<FullOrder | null>(null);
  const [acceptedQuote, setAcceptedQuote] = useState<QuotationDetail | null>(null);

  // Track which step's note / report modal is open (null = closed).
  const [noteStep, setNoteStep] = useState<OrderStep | null>(null);
  const [reportStep, setReportStep] = useState<OrderStep | null>(null);

  async function loadAll() {
    const [o, qs] = await Promise.all([
      api.get<FullOrder>(`/orders/${orderId}`),
      api.get<QuotationSummary[]>(`/orders/${orderId}/quotations`),
    ]);
    setOrder(o);
    const accepted = qs.find((q) => q.status === 'ACCEPTED');
    if (accepted) {
      const d = await api.get<QuotationDetail>(`/orders/${orderId}/quotations/${accepted.id}`);
      setAcceptedQuote(d);
    } else {
      setAcceptedQuote(null);
    }
  }
  useEffect(() => { loadAll(); }, [orderId]);

  // Derive the six quoted-date rows from the accepted schedule. Subsequent steps start at
  // max(user-override, previous-step-end) so user-entered gaps are respected. Delivered is a
  // 0-day milestone pinned to shipping end per spec.
  const rows = useMemo(() => {
    if (!order || !acceptedQuote) return [];
    const s = acceptedQuote.schedule;
    const startDate = fromISODate(s.startDate);
    const supEnd = startDate ? addWorkdays(startDate, s.supplyDays ?? 0) : null;
    const exUser = fromISODate(s.executionStartDate);
    const exStart = supEnd ? (exUser && exUser > supEnd ? exUser : supEnd) : null;
    const exEnd = exStart ? addWorkdays(exStart, s.executionDays ?? 0) : null;
    const prepUser = fromISODate(s.prepStartDate);
    const prepStart = exEnd ? (prepUser && prepUser > exEnd ? prepUser : exEnd) : null;
    const prepEnd = prepStart ? addWorkdays(prepStart, s.prepDays ?? 0) : null;
    const shipUser = fromISODate(s.shippingStartDate);
    const shipStart = prepEnd ? (shipUser && shipUser > prepEnd ? shipUser : prepEnd) : null;
    const shipEnd = shipStart ? addWorkdays(shipStart, s.shippingDays ?? 0) : null;

    const quoted: Record<OrderStep, { start: Date | null; days: number; end: Date | null }> = {
      'Order Started':         { start: startDate, days: 0, end: startDate },
      'Supplying Consumables': { start: startDate, days: s.supplyDays ?? 0, end: supEnd },
      'In Execution':          { start: exStart, days: s.executionDays ?? 0, end: exEnd },
      'Preparing to Ship':     { start: prepStart, days: s.prepDays ?? 0, end: prepEnd },
      'Shipped':               { start: shipStart, days: s.shippingDays ?? 0, end: shipEnd },
      'Delivered':             { start: shipEnd, days: 0, end: shipEnd },
    };

    const actualsByStep = new Map(order.stepActuals.map((a) => [a.step, a] as const));
    return ORDER_STEPS.map((step) => {
      const q = quoted[step];
      const actual = actualsByStep.get(step) ?? {
        step, actualStartDate: null, actualEndDate: null,
        stepNote: null, reportNote: null, reportPaths: [],
      };
      const actualStart = fromISODate(actual.actualStartDate);
      const actualEnd = fromISODate(actual.actualEndDate);
      // countWorkdays goes negative if end < start — clamp to 0 per spec ("min 0, non-editable").
      const actualDuration = actualStart && actualEnd ? Math.max(0, countWorkdays(actualStart, actualEnd)) : null;
      const delta = actualDuration != null ? actualDuration - q.days : null;
      return { step, quoted: q, actual, actualStart, actualEnd, actualDuration, delta };
    });
  }, [order, acceptedQuote]);

  async function setActive(step: OrderStep) {
    modals.openConfirmModal({
      title: `Mark "${step}" as the active step?`,
      children: 'The order will advance to this step. You can still edit actual dates on any row.',
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onConfirm: async () => {
        try {
          const o = await api.put<FullOrder>(`/orders/${orderId}/active-step`, { step });
          setOrder(o);
          notifications.show({ message: `Active step set to ${step}.`, color: 'green' });
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Update failed', color: 'red' });
        }
      },
    });
  }

  async function updateActualDate(step: OrderStep, field: 'actualStartDate' | 'actualEndDate', value: Date | null) {
    const body = { [field]: value ? toISODate(value) : null };
    try {
      const o = await api.put<FullOrder>(`/orders/${orderId}/step-actuals/${encodeURIComponent(step)}`, body);
      setOrder(o);
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Save failed', color: 'red' });
    }
  }

  async function toggleTechnicalDifficulty(step: OrderStep) {
    if (!order) return;
    const isOnThisStep = order.state === 'TECHNICAL_DIFFICULTY' && order.technicalDifficultyStep === step;
    try {
      const o = await api.post<FullOrder>(`/orders/${orderId}/technical-difficulty`, { active: !isOnThisStep, step });
      setOrder(o);
      notifications.show({
        message: isOnThisStep ? 'Technical difficulty cleared.' : `Flagged "${step}" as technically difficult.`,
        color: isOnThisStep ? 'gray' : 'red',
      });
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Update failed', color: 'red' });
    }
  }

  function confirmCancel() {
    modals.openConfirmModal({
      title: 'Cancel this order?',
      children: 'The order will be marked CANCELLED. Actual dates remain visible but are locked.',
      labels: { confirm: 'Cancel Order', cancel: 'Back' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          const o = await api.post<FullOrder>(`/orders/${orderId}/cancel`);
          setOrder(o);
          notifications.show({ message: 'Order cancelled.', color: 'red' });
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Cancel failed', color: 'red' });
        }
      },
    });
  }

  function confirmDeliver() {
    modals.openConfirmModal({
      title: 'Finalize this order?',
      children: 'The order will be marked FINALIZED. You will be returned to the orders list.',
      labels: { confirm: 'Finalize', cancel: 'Back' },
      confirmProps: { color: 'green' },
      onConfirm: async () => {
        try {
          await api.post(`/orders/${orderId}/finalize`);
          notifications.show({ message: 'Order finalized.', color: 'green' });
          navigate('/orders');
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Finalize failed', color: 'red' });
        }
      },
    });
  }

  if (!order) return <Center p="xl"><Loader /></Center>;
  if (!acceptedQuote) {
    // The Start Order flow guarantees an ACCEPTED quotation before status ever leaves
    // 'In Quotation'. If we land here without one the DB has drifted — surface a loud alert
    // rather than rendering empty tables that would mislead the user.
    return (
      <Stack>
        <OrderStatusBar current={order.status} />
        <Alert color="red">
          This order is past the quotation phase but has no accepted quotation. Data integrity issue — ask an admin.
        </Alert>
      </Stack>
    );
  }

  const tdStep = order.state === 'TECHNICAL_DIFFICULTY' ? (order.technicalDifficultyStep as OrderStep | null) : null;
  const locked = order.state === 'CANCELLED' || order.state === 'FINALIZED';
  const activeStep = order.status as OrderStep;
  const ship = order.shipment;
  const shipLine = [ship.street, ship.county, ship.zip, ship.country].filter(Boolean).join(', ');

  return (
    <Stack>
      <OrderStatusBar current={order.status} />

      {order.state !== 'ONGOING' && (
        <Alert color={order.state === 'FINALIZED' ? 'green' : order.state === 'CANCELLED' ? 'red' : 'orange'}>
          {order.state === 'FINALIZED' && 'This order is FINALIZED. All step actions are locked.'}
          {order.state === 'CANCELLED' && 'This order has been CANCELLED. Actual dates are locked.'}
          {order.state === 'TECHNICAL_DIFFICULTY' && (
            <>
              Technical difficulty flagged on <b>{order.technicalDifficultyStep}</b>. Clear it from the Step table to resume normal state.
            </>
          )}
        </Alert>
      )}

      <div className="section">
        <div className="section-header">Order Basics</div>
        <div className="section-body">
          <Group align="flex-start" gap="xl" wrap="wrap">
            <Group gap="sm">
              {order.customerLogo && <img src={imageUrl(order.customerLogo, true)} alt="" style={{ maxHeight: 48 }} />}
              <Stack gap={2}>
                <Group gap={6}>
                  <IconCustomer size={14} />
                  <Text fw={600}>{order.customerName ?? '—'}</Text>
                  {order.firstPartImagePath && (
                    <img
                      src={imageUrl(order.firstPartImagePath, true)}
                      alt="Order"
                      style={{ maxHeight: 36, marginLeft: 6, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }}
                    />
                  )}
                </Group>
                <Text size="xs" c="dimmed">Customer</Text>
              </Stack>
            </Group>
            <Stack gap={2}>
              <Text fw={600}><code>{order.orderUid}</code></Text>
              <Text size="xs" c="dimmed">Order ID</Text>
            </Stack>
            <Stack gap={2}>
              <Text fw={600}>{order.name}</Text>
              <Text size="xs" c="dimmed">Order Name</Text>
            </Stack>
            <Stack gap={2}>
              <Text>{order.contactName ?? '—'}</Text>
              <Text size="xs" c="dimmed">Contact</Text>
            </Stack>
            <Stack gap={2}>
              <Text>{order.technicianName ?? '—'}</Text>
              <Text size="xs" c="dimmed">Technician</Text>
            </Stack>
            <Stack gap={2}>
              <Text fw={600}><code>{acceptedQuote.quotationUid}</code></Text>
              <Text size="xs" c="dimmed">Accepted quote</Text>
            </Stack>
            <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
              <Text>{shipLine || '—'}</Text>
              <Text size="xs" c="dimmed">Shipment address</Text>
            </Stack>
          </Group>
        </div>
      </div>

      <OrderGanttSection
        schedule={acceptedQuote.schedule}
        currentStep={activeStep}
        technicalDifficulty={order.state === 'TECHNICAL_DIFFICULTY'}
        showToday
        defaultOpen
        includeDelivered
      />

      <div className="section">
        <div className="section-header">Order Steps</div>
        <div className="section-body">
          <Paper withBorder>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Active</Table.Th>
                  <Table.Th>Step</Table.Th>
                  <Table.Th style={{ width: 130 }}>Quoted Start</Table.Th>
                  <Table.Th style={{ width: 90 }}>Quoted Days</Table.Th>
                  <Table.Th style={{ width: 130 }}>Quoted End</Table.Th>
                  <Table.Th style={{ width: 160 }}>Actual Start</Table.Th>
                  <Table.Th style={{ width: 160 }}>Actual End</Table.Th>
                  <Table.Th style={{ width: 90 }}>Actual Days</Table.Th>
                  <Table.Th style={{ width: 90 }}>Delta</Table.Th>
                  <Table.Th style={{ width: 140 }}>Technical Difficulty</Table.Th>
                  <Table.Th style={{ width: 110 }}>Note</Table.Th>
                  <Table.Th style={{ width: 110 }}>Report</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => {
                  const Icon = STEP_ICONS[row.step];
                  const isActive = row.step === activeStep;
                  const isTd = tdStep === row.step;
                  const slug = row.step.toLowerCase().replace(/\s+/g, '-');
                  return (
                    <Table.Tr
                      key={row.step}
                      style={isTd ? { background: 'var(--mantine-color-red-0)' } : undefined}
                    >
                      <Table.Td>
                        <Radio
                          data-tour={`step-radio-${slug}`}
                          checked={isActive}
                          readOnly
                          onClick={() => { if (!locked && writable && !isActive) setActive(row.step); }}
                          disabled={!writable || locked}
                          styles={{ radio: { cursor: !writable || locked || isActive ? 'default' : 'pointer' } }}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap={8}>
                          <Icon size={18} />
                          <Text fw={isActive ? 600 : 400}>{row.step}</Text>
                          {isTd && <Badge color="red" variant="filled" size="xs">TD</Badge>}
                        </Group>
                      </Table.Td>
                      <Table.Td>{fmtDate(row.quoted.start)}</Table.Td>
                      <Table.Td>{row.quoted.days}</Table.Td>
                      <Table.Td>{fmtDate(row.quoted.end)}</Table.Td>
                      <Table.Td>
                        <DateInput
                          data-tour={`step-start-${slug}`}
                          value={row.actualStart}
                          onChange={(v) => updateActualDate(row.step, 'actualStartDate', v)}
                          placeholder="—"
                          clearable
                          disabled={!writable || locked}
                          size="xs"
                          valueFormat="YYYY-MM-DD"
                        />
                      </Table.Td>
                      <Table.Td>
                        <DateInput
                          data-tour={`step-end-${slug}`}
                          value={row.actualEnd}
                          onChange={(v) => updateActualDate(row.step, 'actualEndDate', v)}
                          placeholder="—"
                          clearable
                          disabled={!writable || locked}
                          size="xs"
                          valueFormat="YYYY-MM-DD"
                        />
                      </Table.Td>
                      <Table.Td>{row.actualDuration ?? '—'}</Table.Td>
                      <Table.Td>
                        {row.delta == null
                          ? '—'
                          : (
                            <Badge color={deltaColor(row.delta)} variant="light">
                              {row.delta > 0 ? `+${row.delta}` : row.delta}
                            </Badge>
                          )}
                      </Table.Td>
                      <Table.Td>
                        {isActive ? (
                          <Button
                            data-tour="flag-td"
                            size="compact-xs"
                            variant={isTd ? 'filled' : 'light'}
                            color="red"
                            onClick={() => toggleTechnicalDifficulty(row.step)}
                            disabled={!writable || locked}
                          >
                            {isTd ? 'Clear TD' : 'Flag TD'}
                          </Button>
                        ) : null}
                      </Table.Td>
                      {/* Add Note */}
                      <Table.Td>
                        <Tooltip
                          label={row.actual.stepNote ?? undefined}
                          disabled={!row.actual.stepNote}
                          multiline
                          maw={300}
                          withArrow
                        >
                          <Button
                            data-tour={`step-note-${slug}`}
                            size="compact-xs"
                            variant={row.actual.stepNote ? 'filled' : 'light'}
                            color="blue"
                            onClick={() => setNoteStep(row.step)}
                            disabled={!writable || locked}
                          >
                            {row.actual.stepNote ? 'Note ✓' : 'Add Note'}
                          </Button>
                        </Tooltip>
                      </Table.Td>
                      {/* Add Report */}
                      <Table.Td>
                        <Tooltip
                          label={
                            row.actual.reportNote
                              ? `${row.actual.reportNote}${row.actual.reportPaths.length > 0 ? ` · ${row.actual.reportPaths.length} path(s)` : ''}`
                              : row.actual.reportPaths.length > 0
                                ? `${row.actual.reportPaths.length} path(s)`
                                : undefined
                          }
                          disabled={!row.actual.reportNote && row.actual.reportPaths.length === 0}
                          multiline
                          maw={320}
                          withArrow
                        >
                          <Button
                            data-tour={`step-report-${slug}`}
                            size="compact-xs"
                            variant={(row.actual.reportNote || row.actual.reportPaths.length > 0) ? 'filled' : 'light'}
                            color="teal"
                            onClick={() => setReportStep(row.step)}
                            disabled={!writable || locked}
                          >
                            {(row.actual.reportNote || row.actual.reportPaths.length > 0) ? 'Report ✓' : 'Add Report'}
                          </Button>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        </div>
      </div>

      <Group justify="space-between" mt="md">
        <Button
          data-tour="cancel-order"
          color="red"
          variant="light"
          onClick={confirmCancel}
          disabled={!writable || locked}
        >
          Cancel Order
        </Button>
        {activeStep === 'Delivered' && !locked && (
          <Button
            data-tour="finalize-order"
            color="green"
            size="lg"
            onClick={confirmDeliver}
            disabled={!writable}
          >
            Order Delivered — Finalize
          </Button>
        )}
      </Group>

      {/* Step Note modal */}
      {noteStep && (() => {
        const sa = order.stepActuals.find((a) => a.step === noteStep);
        return (
          <StepNoteModal
            orderId={orderId}
            step={noteStep}
            existingNote={sa?.stepNote ?? null}
            onClose={() => setNoteStep(null)}
            onSaved={(updated) => { setOrder(updated); setNoteStep(null); }}
          />
        );
      })()}

      {/* Step Report modal */}
      {reportStep && (() => {
        const sa = order.stepActuals.find((a) => a.step === reportStep);
        return (
          <StepReportModal
            orderId={orderId}
            step={reportStep}
            existingNote={sa?.reportNote ?? null}
            existingPaths={sa?.reportPaths ?? []}
            onClose={() => setReportStep(null)}
            onSaved={(updated) => { setOrder(updated); setReportStep(null); }}
          />
        );
      })()}
    </Stack>
  );
}
